import { useId, useState } from "react";
import { Input } from "antd";
import { Trans } from "react-i18next";
import { t } from "i18next";
import classNames from "classnames";
import { IconLoading } from "@icons/index";
import { useLinkAutoParse } from "@hooks/useLinkAutoParse";
import { isDevelopment } from "@utils/env";

export interface LinkInputStepProps {
  parsing: boolean;
  error: string | null;
  /** 解析链接由父组件负责完成所有副作用。aborted 时父组件应跳过所有副作用。 */
  onParse: (url: string, signal: AbortSignal) => Promise<boolean>;
  onSwitchToManual: () => void;
}

/**
 * Create Product Entity Modal 的链接输入态。
 *
 * 视觉与 CreateCharacterEntityModal/LinkInputStep 对齐（节点 3536:23389）：56px 高、16px 加粗
 * `https://` prefix、独立 1px 分隔条。Product 独有的失败兜底「Try to upload an image」保留。
 * 输入即自动解析（debounce 450ms），按 Enter 立即触发；switchMap 保证以最后一次输入为准。
 */
export const LinkInputStep = ({ parsing, error, onParse, onSwitchToManual: _onSwitchToManual }: LinkInputStepProps) => {
  const [url, setUrl] = useState(
    isDevelopment()
      ? "https://www.kalodata.com/product/detail?id=1731831763391320673&language=zh-CN&currency=CNY&region=US&dateRange=%5B%222026-03-02%22%2C%222026-03-31%22%5D&cateValue=%5B%5D"
      : "",
  );
  const id = useId();
  const { submit, submitImmediately } = useLinkAutoParse(onParse);

  return (
    <div className="pt-[8px] flex flex-col gap-[8px]">
      <div
        className={classNames(
          "flex items-center rounded-[8px] bg-kds-bg-element px-[12px] h-[56px] border border-solid",
          error ? "border-kds-danger-border" : "border-kds-border-subtle",
        )}
      >
        <span className="text-[16px] text-kds-fg font-semibold shrink-0 leading-[24px]">https://</span>
        <span className="shrink-0 w-px h-[48px] bg-kds-border-solid mx-[8px]" />
        <Input
          id={id}
          value={url}
          onChange={(e) => {
            setUrl(e.target.value);
            submit(e.target.value);
          }}
          onPressEnter={() => submitImmediately(url)}
          variant="borderless"
          placeholder={t("Paste TikTok, Amazon (US, UK, JP, DE), or Kalodata product link")}
          className="flex-1 bg-transparent! px-0! h-[24px] text-[14px] text-kds-fg placeholder:text-kds-fg-subtle"
          autoFocus
        />
        {parsing && <IconLoading className="w-[16px] h-[16px] animate-spin text-kds-fg-soft ml-[8px]" />}
      </div>
      {error && (
        <div className="text-[12px] leading-[16px] text-kds-danger-fg">
          {"* "}
          <Trans
            components={{
              span: (
                <span
                  className="text-kds-fg cursor-pointer underline"
                  onClick={() => {
                    // todo(leon): onTryUploadImage
                    console.warn("todo(leon): onTryUploadImage");
                  }}
                />
              ),
            }}
            defaults="Unable to retrieve product information. Please <span>Try to upload an image</span>"
          ></Trans>
        </div>
      )}
    </div>
  );
};
